import {
  Button,
  Checkbox,
  DatePicker,
  Drawer,
  Form,
  Input,
  Select,
  Table,
} from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import isEqual from "lodash/isEqual";

import { enumFieldType, fieldTypes } from "../../constant/WebToLead";
import PickList from "../web2lead/PickList";
import { LEAD_CUSTOM_FIELD_SCOPE } from "../../utils/leadCustomFields";

const LeadCustomFieldFormDrawer = ({
  open,
  onClose,
  onSubmit,
  initialValues,
  existingFieldNames,
  canManage,
  submitting,
}) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [type, setType] = useState(initialValues?.fieldType);
  const [error, setError] = useState();

  const [initialFormValues, setInitialFormValues] = useState();
  const watchedValues = Form.useWatch([], form);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setType(undefined);
      setError(undefined);
      setInitialFormValues(undefined);
      return;
    }

    const payload = initialValues?.fieldName
      ? {
          fieldType: initialValues.fieldType,
          fieldName: initialValues.fieldName,
          defaultValue:
            initialValues?.fieldType === enumFieldType.DATE &&
            initialValues?.defaultValue
              ? dayjs(initialValues.defaultValue)
              : initialValues?.defaultValue,
          isMandatory: !initialValues?.isOptional,
          pickValues: initialValues?.pickValues || [],
        }
      : {
          fieldType: undefined,
          fieldName: undefined,
          defaultValue: undefined,
          isMandatory: false,
          pickValues: [],
        };

    form.setFieldsValue(payload);
    setInitialFormValues(payload);
    setType(initialValues?.fieldType);
    setError(undefined);
  }, [form, initialValues, open]);

  const isChanged = useMemo(() => {
    if (!initialValues?.fieldName || !initialFormValues) return true;

    const relevantFields = [
      "fieldType",
      "fieldName",
      "defaultValue",
      "isMandatory",
      "pickValues",
    ];

    return !relevantFields.every((key) => {
      const v1 = watchedValues?.[key];
      const v2 = initialFormValues?.[key];

      if (dayjs.isDayjs(v1) && dayjs.isDayjs(v2)) {
        return v1.isSame(v2);
      }
      if (key === "pickValues") {
        return isEqual(v1 || [], v2 || []);
      }
      return v1 === v2;
    });
  }, [watchedValues, initialFormValues, initialValues?.fieldName]);

  const handleFinish = (values) => {
    if (
      [enumFieldType.PICK_LIST, enumFieldType.MULTI_PICK_LIST].includes(
        values?.fieldType,
      ) &&
      !values?.pickValues?.length
    ) {
      setError("PickList Values is required");
      return;
    }

    onSubmit({
      ...initialValues,
      ...values,
      defaultValue:
        values?.fieldType === enumFieldType.DATE && values?.defaultValue
          ? dayjs(values.defaultValue).format()
          : values?.defaultValue,
      isOptional: !values?.isMandatory,
      isSelected: true,
      fieldCategory: "custom",
      fieldScope: LEAD_CUSTOM_FIELD_SCOPE,
    });
  };

  return (
    <Drawer
      width={700}
      zIndex={10000}
      closeIcon={false}
      open={open}
      onClose={onClose}
      title={
        <div className="d-flex justify-content-between">
          <div className="heading24px roboto-medium text-color-272727">
            {initialValues?.fieldName ? "Edit" : "Create"} Lead Custom Field
          </div>
          <CloseOutlined style={{ color: "#393939" }} onClick={onClose} />
        </div>
      }
    >
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <div className="web-to-lead">
          <Form.Item
            name="fieldType"
            label={<span className="tw-text-xs tw-font-bold tw-text-slate-600">Field Type</span>}
            style={{ marginBottom: 0 }}
            rules={[{ required: true, message: "Please select a field type" }]}
          >
            <Select
              className="tw-modal-select"
              placeholder="Select Field Type"
              disabled={!canManage}
              options={fieldTypes}
              onChange={(value) => {
                form.setFieldsValue({
                  fieldType: value,
                  defaultValue: undefined,
                  pickValues: undefined,
                  fieldLength: undefined,
                });
                setType(value);
                setError(undefined);
              }}
            />
          </Form.Item>

          <div className="mt30">
            <Form.Item
              name="fieldName"
              label={<span className="tw-text-xs tw-font-bold tw-text-slate-600">Label Name</span>}
              style={{ marginBottom: 0 }}
              rules={[
                { required: true, message: "Label Name is required" },
                () => ({
                  validator(_, value) {
                    if (!value?.trim()) {
                      return Promise.resolve();
                    }

                    if (
                      existingFieldNames.some(
                        (name) =>
                          name.toLowerCase() === value.trim().toLowerCase(),
                      )
                    ) {
                      return Promise.reject("Label Name must be unique");
                    }

                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <Input className="tw-modal-input" disabled={!canManage} />
            </Form.Item>
          </div>


          {[enumFieldType.PICK_LIST, enumFieldType.MULTI_PICK_LIST].includes(
            type,
          ) && (
            <div className="mt30">
              <PickList
                form={form}
                error={error}
                setError={setError}
                disabled={!canManage}
              />
              {error && (
                <div className="pb-2" style={{ color: "#ff4d4f" }}>
                  {error}
                </div>
              )}
            </div>
          )}

          {![enumFieldType.PICK_LIST, enumFieldType.MULTI_PICK_LIST].includes(
            type,
          ) && (
            <div className="mt30">
              <Form.Item
                name="defaultValue"
                style={{ marginBottom: 0 }}
                label={<span className="tw-text-xs tw-font-bold tw-text-slate-600">Default Value</span>}
              >
                {type === enumFieldType.DATE ? (
                  <DatePicker
                    style={{ width: "100%" }}
                    className="tw-modal-input"
                    disabled={!canManage}
                  />
                ) : (
                  <Input
                    type={type === enumFieldType.INTEGER ? "number" : undefined}
                    className="tw-modal-input"
                    disabled={!canManage}
                  />
                )}
              </Form.Item>
            </div>
          )}

          <div className="text-right">
            <Form.Item
              name="isMandatory"
              style={{ marginBottom: 0 }}
              valuePropName="checked"
            >
              <Checkbox className="heading15px mt20" disabled={!canManage}>
                Mandatory Field
              </Checkbox>
            </Form.Item>
          </div>


          <div className="mt30 tw-flex tw-justify-end tw-gap-3">
            <Button
              className="!tw-h-[38px] !tw-rounded-lg !tw-border-slate-200 !tw-bg-white !tw-px-5 !tw-text-xs !tw-font-bold !tw-text-slate-500 hover:!tw-border-slate-300 hover:!tw-text-slate-700 tw-transition-all tw-duration-200"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              disabled={!canManage || (initialValues?.fieldName && !isChanged)}
              className="!tw-h-[38px] !tw-rounded-lg !tw-bg-theme !tw-border-theme !tw-text-white tw-text-xs tw-font-bold tw-px-5 tw-shadow-sm hover:tw-shadow-md tw-transition-all tw-duration-200"
            >
              {initialValues?.fieldName ? "Update" : "Save"}
            </Button>
          </div>
        </div>
      </Form>
    </Drawer>
  );
};

export default LeadCustomFieldFormDrawer;
